import {CropYieldForecast} from '@6grain/masofa-monolith-api-client/ai-inference';
import {Season} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {ymdFromApiDate} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/managers/field-details-date.util';

export const YIELD_FORECAST_EMPTY = '—';

export interface YieldForecastGeneralModel {
  crop: string;
  variety: string;
  plantingDate: string;
  harvestDate: string;
  harvest: string;
  yieldHa: string;
  area: string;
  calcDate: string;
  pessimistic: string;
  likely: string;
  optimistic: string;
  title: string;
  subtitle: string;
}

export interface YieldForecastGeneralInput {
  forecast: CropYieldForecast | null;
  cropName: string | null | undefined;
  varietyName: string | null | undefined;
  fieldName: string | null | undefined;
  regionName: string | null | undefined;
  districtName: string | null | undefined;
  season: Season | null;
  formatNumber?: (value: number) => string;
}

/** First finite number, including 0. Null and non-finite values fall through. */
export function coalesceNumber(...values: (number | null | undefined)[]): number | null {
  for (const value of values) {
    if (value != null && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

export function formatDisplayDate(raw: string | null | undefined, withTime = false): string {
  if (!raw) {
    return YIELD_FORECAST_EMPTY;
  }
  const ymd = ymdFromApiDate(raw);
  if (!ymd) {
    return YIELD_FORECAST_EMPTY;
  }
  const [year, month, day] = ymd.split('-');
  const date = `${day}.${month}.${year}`;
  if (!withTime) {
    return date;
  }
  const time = raw.match(/T(\d{2}):(\d{2})/);
  return time ? `${date} ${time[1]}:${time[2]}` : date;
}

export function formatCalcDate(raw: string | null | undefined): string {
  if (!raw) {
    return YIELD_FORECAST_EMPTY;
  }
  return formatDisplayDate(raw, raw.includes('T'));
}

export function buildSeasonTitle(crop: string | null | undefined, field: string | null | undefined): string {
  const cropName = textOrEmpty(crop);
  const fieldName = textOrEmpty(field);
  if (cropName === YIELD_FORECAST_EMPTY && fieldName === YIELD_FORECAST_EMPTY) {
    return YIELD_FORECAST_EMPTY;
  }
  return `${cropName} · ${fieldName}`;
}

export function buildSeasonSubtitle(
  region: string | null | undefined,
  district: string | null | undefined,
  planting: string,
  harvest: string,
): string {
  const place = [region, district]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part.length > 0 && part !== YIELD_FORECAST_EMPTY)
    .join(', ');
  const dates = `${planting} — ${harvest}`;
  const datesMissing = planting === YIELD_FORECAST_EMPTY && harvest === YIELD_FORECAST_EMPTY;
  if (place && datesMissing) {
    return place;
  }
  if (place) {
    return `${place} · ${dates}`;
  }
  return datesMissing ? YIELD_FORECAST_EMPTY : dates;
}

export function buildYieldForecastGeneral(input: YieldForecastGeneralInput): YieldForecastGeneralModel {
  const season = input.season;
  const forecast = input.forecast;
  const format = input.formatNumber ?? ((value: number) => value.toFixed(2));
  const plantingRaw = season?.plantingDate || season?.plantingDatePlan || null;
  const harvestRaw = season?.harvestingDate || season?.harvestingDatePlan || null;
  const plantingDate = formatDisplayDate(plantingRaw);
  const harvestDate = formatDisplayDate(harvestRaw);
  const calcRaw = forecast?.forecastDate || season?.yieldForecastCalculationDate || null;

  return {
    crop: textOrEmpty(input.cropName),
    variety: textOrEmpty(input.varietyName),
    plantingDate,
    harvestDate,
    harvest: formatAmount(coalesceNumber(forecast?.harvest, forecast?.forecastAbsolute), format),
    yieldHa: formatAmount(coalesceNumber(season?.yieldHaPlan, forecast?.yield), format),
    area: formatAmount(coalesceNumber(forecast?.area, season?.fieldArea), format),
    calcDate: formatCalcDate(calcRaw),
    pessimistic: formatAmount(coalesceNumber(season?.yieldHaPessimistic), format),
    likely: formatAmount(coalesceNumber(season?.yieldHaPlan), format),
    optimistic: formatAmount(coalesceNumber(season?.yieldHaOptimistic), format),
    title: buildSeasonTitle(input.cropName, input.fieldName),
    subtitle: buildSeasonSubtitle(input.regionName, input.districtName, plantingDate, harvestDate),
  };
}

export function resolveWeatherPoint(
  season: Pick<Season, 'latitude' | 'longitude' | 'polygonJson'> | null,
  fieldPolygonJson: string | null,
): {lat: number; lng: number} | null {
  const lat = season?.latitude;
  const lng = season?.longitude;
  if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
    return {lat, lng};
  }
  return polygonCenter(season?.polygonJson) ?? polygonCenter(fieldPolygonJson);
}

function polygonCenter(polygonJson: string | null | undefined): {lat: number; lng: number} | null {
  if (!polygonJson) {
    return null;
  }
  const coords = PolygonConverterUtil.polygonJsonToCoordinates(polygonJson);
  return PolygonConverterUtil.getPolygonCenter(coords);
}

function formatAmount(value: number | null, format: (value: number) => string): string {
  return value == null ? YIELD_FORECAST_EMPTY : format(value);
}

function textOrEmpty(value: string | null | undefined): string {
  const text = value?.trim();
  return text ? text : YIELD_FORECAST_EMPTY;
}
